import {
  Button,
  Flex,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";

import {
  spareParametersBySpareID,
  getParameter,
  listParameters,
  getSpare,
} from "graphql/queries";
import { updateSpareParameter } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import SpareParamatersTable from "components/spares/parameters/SpareParametersTable";
import SpareParameterCreateForm from "./SpareParameterCreateForm";
import SpareParameterUpdateForm from "./SpareParameterUpdateForm";

const SpareParameters = ({ spareID, spareName, visible, onClose }) => {
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "spare:create-param",
    "spare:update-param",
    "spare:delete-param",
  ]);
  const [visibleState, setVisibleState] = useState(visible);
  const [spareParameters, setSpareParameters] = useState([]);
  const [spareParametersLoading, setSpareParametersLoading] = useState(false);
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    spareParameterId: null,
    spareParameterName: null,
  });
  const [modalSpareParameter, setModalSpareParameter] = useState({
    visible: false,
    spareParameterId: null,
  });

  const columnsMapped = useMemo(
    () =>
      spareParameters.map((item) => {
        return {
          ...item,
          parameterName: item.parameterNameStr || <Loader />,
          actions: (
            <>
              {canUpdate && (
                <Button
                  className="px-1"
                  children={strings.actions.edit}
                  variation="link"
                  size="small"
                  onClick={() => openModal("update", item.id)}
                />
              )}
              {canDelete && (
                <Button
                  className="px-1"
                  children={strings.actions.delete}
                  variation="link"
                  size="small"
                  onClick={() => {
                    setModalDetails({
                      visible: true,
                      spareParameterId: item.id,
                      spareParameterName: item.name,
                    });
                  }}
                />
              )}
            </>
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [spareParameters]
  );

  const onNext = () => {
    fetchSpareParameters(paginationTokens.nextToken);
  };

  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchSpareParameters(previous, false);
  };

  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchSpareParameters(undefined, undefined, keywordSplit);
  };

  const openModal = (action, id) => {
    setVisibleState(false);
    setModalSpareParameter({
      visible: true,
      action,
      spareParameterId: id,
    });
  };

  const closeModal = () => {
    setVisibleState(true);
    setModalSpareParameter({
      visible: false,
    });
  };

  const onSuccessAction = () => {
    closeModal();
    fetchSpareParameters();
  };

  async function fetchParameterName(id) {
    try {
      const parameterData = await API.graphql(
        graphqlOperation(getParameter, {
          id,
        })
      );
      const parameterItem = parameterData.data.getParameter;
      return parameterItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchParameterName", err);
      return "";
    }
  }

  const getParameterNames = (list) => {
    list?.forEach(async (item) => {
      const parameterNameStr = await fetchParameterName(item.parameterID);

      setSpareParameters((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          parameterNameStr,
        };

        return updatedList;
      });
    });
  };

  async function getCompany(id) {
    const dataReturned = await API.graphql(graphqlOperation(getSpare, { id }));
    const itemReturned = dataReturned.data.getSpare;
    if (!itemReturned) {
      toast.error(strings.responses.errorFetch);
      return;
    }
    return itemReturned.companyID;
  }

  async function fetchSpareParameters(token, isNext = true, keywords) {
    try {
      setSpareParametersLoading(true);
      const limitMapped = 10;
      let filterObjList = [];

      if (keywords) {
        const companyID = await getCompany(spareID);
        const parametersData = await API.graphql(
          graphqlOperation(listParameters, {
            filter: {
              and: keywords.map((key) => ({
                or: [
                  {
                    name: { contains: key },
                  },
                ],
              })),
              companies: { contains: companyID },
              deletedAt: { attributeExists: false },
            },
          })
        );
        const parametersItems = parametersData.data.listParameters.items;

        filterObjList =
          parametersItems?.map((param) => ({
            parameterID: { eq: param.id },
          })) ?? [];
      }

      const spareParametersData = await API.graphql(
        graphqlOperation(spareParametersBySpareID, {
          spareID,
          limit: !keywords ? limitMapped : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    ...filterObjList,
                    {
                      parameterValue: { contains: key },
                    },
                  ],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const spareParametersItems =
        spareParametersData.data.spareParametersBySpareID.items;
      setSpareParameters(spareParametersItems);
      getParameterNames(spareParametersItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken:
            spareParametersData.data.spareParametersBySpareID.nextToken,
        };
      });
      setSpareParametersLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setSpareParametersLoading(false);
    } finally {
      setSpareParametersLoading(false);
    }
  }

  async function spareParameterDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateSpareParameter, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateSpareParameter.id;
      setSpareParameters(
        spareParameters.filter((item) => item.id !== deletedId)
      );
      setModalDetails({
        visible: false,
        spareParameterId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchSpareParameters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setVisibleState(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <>
      <CModal
        size="xl"
        alignment="center"
        visible={visibleState}
        onClose={() => !modalSpareParameter.visible && onClose()}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>
            {strings.title} - {spareName}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          <ScrollView maxHeight="75vh">
            <div>
              <div className="p-3">
                <Flex className="mb-1" justifyContent="space-between">
                  <div style={styles.searchByKeyword}>
                    <SearchField
                      label={strings.table.search.label}
                      placeholder={strings.table.search.placeholder}
                      onSubmit={onKeywordSubmit}
                      onClear={onKeywordSubmit}
                    />
                  </div>
                  {canCreate && (
                    <Button
                      style={styles.buttonAdd}
                      children={strings.actions.addSpareParameter}
                      variation="primary"
                      onClick={() => openModal("create")}
                    />
                  )}
                </Flex>
                {spareParametersLoading ? (
                  <Loader />
                ) : (
                  <SpareParamatersTable
                    items={columnsMapped}
                    pagination={paginationTokens}
                    onNext={onNext}
                    onPrevious={onPrevious}
                  />
                )}
              </div>
              <ConfirmModal
                visible={modalDetails.visible}
                body={strings.formatString(
                  strings.modal.confirmDeleteQuestion,
                  {
                    name: modalDetails.spareParameterName,
                  }
                )}
                onCancel={() =>
                  setModalDetails({
                    visible: false,
                    spareParameterId: null,
                  })
                }
                onConfirm={() =>
                  spareParameterDelete(modalDetails.spareParameterId)
                }
              />
            </div>
          </ScrollView>
        </CModalBody>
      </CModal>
      {modalSpareParameter.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalSpareParameter.visible}
          onClose={closeModal}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalSpareParameter.action === "create" ? (
                <>
                  {strings.modalSpareParameter.addTitle} - {spareName}
                </>
              ) : (
                <>
                  {strings.modalSpareParameter.updateTitle} - {spareName}
                </>
              )}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalSpareParameter.action === "create" ? (
                <SpareParameterCreateForm
                  spareID={spareID}
                  onSuccess={onSuccessAction}
                />
              ) : (
                <SpareParameterUpdateForm
                  spareID={spareID}
                  spareParameterId={modalSpareParameter.spareParameterId}
                  onSuccess={onSuccessAction}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Parámetros de Repuesto",
    successDeleted: "Parámetro de Repuesto eliminado con éxito",
    actions: {
      addSpareParameter: "Agregar Parámetro de Repuesto",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar parámetros de repuesto",
    },
    table: {
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar el parámetro de repuesto {name}?",
    },
    modalSpareParameter: {
      addTitle: "Agregar Parámetro de Repuesto",
      updateTitle: "Actualizar Parámetro de Repuesto",
    },
  },
});

const styles = {
  buttonAdd: { height: "fit-content" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default SpareParameters;
